import { type ReactNode } from 'react';
import { ArrowUp, Brain } from 'lucide-react';
import { HoverTip, TIP_BADGE } from './HoverTip';
import { clock } from './waitClock';

// A circled brain that reveals a model's reasoning on hover. Shared by Phase 1 (per-page
// cell), Phase 1 (the PAGE row of the parts table) and the HOLES row's per-part search.
// The hovering itself — the panel that escapes a scrolling table and stays on screen — is
// HoverTip's, so this file is only about what the icon means.

// The two-section body every notice badge shows: the model's reasoning over its raw
// reply. One definition so a new badge can't invent its own heading or forget the
// raw output — the raw is what you fall back on when the reasoning explains nothing.
// ONE SECTION of such a body: a heading and a block of text under it. Exported because a
// badge that needs a THIRD section — what the answer was, what the call cost — must not
// invent its own heading style beside these two, which is exactly the drift the shared
// definition above exists to stop.
// THE SMALL HEADING OVER ONE OF THOSE SECTIONS, as a class rather than an element, since
// the panels that need it wrap it in their own layout. One definition: it was written out
// three times, and the copies had already drifted to a dark grey that read as switched-off
// text on the dark cards this app puts them on. Slate-400 either way — a label over a
// block, never as dim as the block itself.
export const TIP_LABEL = 'text-[11px] font-bold uppercase tracking-wide text-slate-500';

// THE TEXT OF A CALL, whichever half: the prompt, the reasoning, the raw output. One style, so
// the question and its answer read alike in the same window — monospace, small, airy, spaces
// kept (see PromptPanel, and tipSection below). The answer was body text at the size of the
// page around it, three sizes larger than the prompt over it.
export const CALL_TEXT = 'whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed';

export function tipSection(title: string, body: string): ReactNode {
  return (
    <div>
      <div className={`mb-1 ${TIP_LABEL}`}>{title}</div>
      {/* pre-WRAP, not pre-line: some of these raw sections are JSON, and pre-line keeps the
          newlines while collapsing the runs of spaces that are the indentation. A pretty
          printed object came out as a flat stack of unaligned lines. */}
      <div className={CALL_TEXT}>{body.trim() || '—'}</div>
    </div>
  );
}

// HOW LONG THE CALL TOOK, above everything, where a caller has the number. Optional and last
// in the signature because fifteen panels open one of these and only the ones that time their
// call can answer it — a heading reading "—" on the other fourteen would be worse than no
// heading, since "nobody measured it" and "it was instant" are not the same claim.
//
// NOT A SECTION OF ITS OWN. The two below are blocks of text you read; this is one number, and
// giving it the same heading treatment would put a label as tall as its content at the top of
// the panel. It sits on one line, dim, and the eye passes it on the way to the reasoning.
// THE RAW REPLY, BROKEN WHERE ITS OWN THOUGHTS BREAK.
//
// A step's answer is one line of JSON several thousand characters long, and wrapped into a
// panel it is a wall: fourteen holes with the same eleven keys each, and no way to see where
// one ends and the next begins. Finding the hole you are arguing with meant reading for
// `"id"` and counting braces.
//
// SO ARRAYS BREAK AND OBJECTS DO NOT. One line per element — per hole, per fill — and every
// hole entire on its line, keys in the order the model wrote them. Which is the shape of the
// thing: a hole is one thought, and a list of them is a list.
//
// NOT `JSON.stringify(x, null, 2)`, which is the obvious answer and the wrong one here: it
// puts every KEY on its own line, so one hole becomes thirteen and fourteen holes are two
// hundred lines of column. The same argument as for triples of numbers,
// one level up.
//
// AND A LIST WHOSE EVERY ELEMENT NAMES A PART IS DRAWN AS THE PARTS IT IS ABOUT: one entry
// per part, its own holes under it, `part` lifted out of each element and into the key it
// repeats. Fourteen bores across three rails read as three groups you can skip past, not as
// fourteen lines you have to read the fourth field of to know whose they are. It also buys the
// line back that made each hole wrap in the first place — "part":"03", eleven characters, once
// per hole.
//
// STILL THE REPLY, AND ONE STEP FURTHER FROM RAW THAN IT WAS. Whitespace used to be all that
// moved; now one key changes place — never its value, and never any other key. Anything that
// does not parse is still shown exactly as it arrived, which is what a truncated or non-JSON
// answer needs most.
//
// AND A REPLY IN A CODE FENCE IS STILL JSON. Every one of these prompts asks for "no code
// fence" and most models oblige, but not all of them and not every time — and the one that
// wrapped its answer in ```json was the one whose answer was longest, so the only reply that
// needed breaking apart was the only one shown as it arrived. The fence is peeled, once, and
// only when what is left parses; anything else is still shown exactly as it came.
const unfence = (raw: string): string => {
  const fenced = raw.trim().match(/^```[^\n]*\n([\s\S]*?)\n?```$/);
  return fenced ? fenced[1] : raw;
};

const readable = (raw: string): string => {
  let value: unknown;
  try {
    value = JSON.parse(unfence(raw));
  } catch {
    return raw;
  }
  // An object holding nothing but scalars and small objects is one thought; only a list makes
  // it worth breaking apart. The test is recursive because a hole's `u` is an object too, and
  // a hole with one in it still belongs on one line.
  const holdsList = (v: unknown): boolean => {
    if (Array.isArray(v)) return true;
    if (!v || typeof v !== 'object') return false;
    return Object.values(v).some(holdsList);
  };
  // A LIST OF THINGS THAT EACH SAY WHICH PART THEY ARE IN, GROUPED BY IT — or the list back
  // untouched when they do not all say, since a half-grouped list is worse than a flat one.
  //
  // A Map and not an object, which would be the obvious carrier and would quietly re-order the
  // groups: JavaScript sorts integer-like keys ahead of the rest, so parts "03" and "10" in a
  // manual that pads some ids and not others would come out in an order the model never wrote.
  // A Map keeps the order the holes arrived in, which is the order the step works in.
  const byPart = (rows: unknown[]): Map<string, unknown[]> | null => {
    const named = rows.every((r) => !!r && typeof r === 'object' && !Array.isArray(r)
      && ['string', 'number'].includes(typeof (r as Record<string, unknown>).part));
    if (!rows.length || !named) return null;
    const out = new Map<string, unknown[]>();
    for (const row of rows as Record<string, unknown>[]) {
      const { part, ...rest } = row;
      const key = String(part);
      const had = out.get(key);
      if (had) had.push(rest); else out.set(key, [rest]);
    }
    return out;
  };
  const fmt = (v: unknown, pad: string): string => {
    const step = `${pad}  `;
    if (Array.isArray(v)) {
      if (!v.length) return '[]';
      const grouped = byPart(v);
      if (grouped) return fmt(grouped, pad);
      return `[\n${v.map((e) => step + fmt(e, step)).join(',\n')}\n${pad}]`;
    }
    // The grouping above, drawn like the object it stands for. Always broken apart: it exists
    // because the flat list was too long to read, so a one-line form of it would undo itself.
    if (v instanceof Map) {
      return `{\n${[...v]
        .map(([k, e]) => `${step}${JSON.stringify(k)}: ${fmt(e, step)}`)
        .join(',\n')}\n${pad}}`;
    }
    if (v && typeof v === 'object') {
      const rows = Object.entries(v);
      if (!rows.length) return '{}';
      if (!holdsList(v)) return JSON.stringify(v);
      return `{\n${rows
        .map(([k, e]) => `${step}${JSON.stringify(k)}: ${fmt(e, step)}`)
        .join(',\n')}\n${pad}}`;
    }
    return JSON.stringify(v);
  };
  return fmt(value, '');
};

export function reasoningTooltip(
  thoughts: string,
  raw: string,
  t: (k: string) => string,
  took?: number | null,
  // NOTHING IN THE REPLY BELOW COULD BE READ. Said here rather than left to be inferred from
  // the text: a truncated list of rectangles looks like a list of rectangles at a glance, and
  // whatever the run drew from it — usually nothing — is what the table is now reporting as
  // the model's answer.
  unreadable?: boolean,
  // WHAT TO SAY WHERE THERE IS NO REASONING, when the caller knows why — see FindHoles, which
  // knows how much the model thought. The generic line otherwise.
  noThoughts?: string,
): ReactNode {
  return (
    <div className="space-y-3">
      {took != null && (
        <div className={TIP_LABEL}>
          {t('notice.cat.tookTime').replace('{t}', clock(took))}
        </div>
      )}
      {tipSection(t('notice.cat.reasoningTitle'), thoughts || noThoughts || t('notice.cat.noReasoning'))}
      {unreadable && (
        <div className="rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-sm text-amber-200">
          {t('notice.cat.rawUnreadable')}
        </div>
      )}
      {tipSection(t('notice.cat.rawOutputTitle'), readable(raw))}
    </div>
  );
}

export function ReasoningBadge({
  content,
  label,
  reasons,
}: {
  content: ReactNode;
  label: string;
  reasons: boolean;
}) {
  return (
    <HoverTip
      content={content}
      trigger={
        <span className={TIP_BADGE}>
          {reasons ? (
            <Brain className="h-3 w-3" aria-label={label} />
          ) : (
            <ArrowUp className="h-3 w-3" aria-label={label} />
          )}
        </span>
      }
    />
  );
}
